/**
 * useMovementAnalysis Hook
 *
 * SLAM backend rewrite of the Firebase-based movement analysis hook.
 * Uses SLAM backend for video upload and Gemini analysis.
 *
 * R6: Requests structured JSON from Gemini (scores + timestamped findings).
 */
import { useState, useCallback } from 'react';
import { NODE_BACKEND_URL } from '../utils/constant';
import { getUser } from '../utils/helpers';

/**
 * Structured prompt requesting Gemini to return JSON with composite scores
 * and timestamped findings for progressive disclosure (R6, R7, R8).
 */
const STRUCTURED_ANALYSIS_PROMPT = `
You are an expert movement analysis coach. Analyze the uploaded video and provide feedback.

IMPORTANT: You MUST respond with valid JSON in EXACTLY this format (no markdown fences, no extra text):
{
  "scores": {
    "formQuality": <number 0-10>,
    "movementEfficiency": <number 0-10>,
    "riskLevel": "<low|medium|high>"
  },
  "findings": [
    {
      "timestamp": "<MM:SS>",
      "category": "<form|efficiency|safety|technique>",
      "severity": "<info|warning|error>",
      "description": "<what was observed>",
      "recommendation": "<actionable coaching cue>"
    }
  ],
  "detailedAnalysis": "<comprehensive multi-paragraph analysis text>"
}

Scoring Guidelines:
- formQuality: 0-3.9 = poor (red), 4-6.9 = needs work (amber), 7-10 = good (green)
- movementEfficiency: same scale as formQuality
- riskLevel: "low" (safe to continue), "medium" (modify loading), "high" (stop and correct)

Include timestamps for every notable observation. Be specific and actionable.
`;

function getAuthHeaders() {
  try {
    const userData = sessionStorage.getItem('user');
    const token = userData ? JSON.parse(userData)?.token : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

export function useMovementAnalysis() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [question, setQuestion] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null); // { scores, findings, detailedAnalysis }
  const [analysisMetadata, setAnalysisMetadata] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [savedAnalyses, setSavedAnalyses] = useState([]);

  // Parse Gemini's response into structured format
  const parseStructuredResponse = (responseText) => {
    try {
      // Try to extract JSON from the response
      let jsonStr = responseText;
      // Handle case where Gemini wraps in markdown code fences
      const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        jsonStr = jsonMatch[1].trim();
      }
      const parsed = JSON.parse(jsonStr);
      if (parsed.scores && parsed.findings && parsed.detailedAnalysis) {
        return parsed;
      }
    } catch {
      // Fallback: return raw text as detailed analysis with default scores
    }
    return {
      scores: { formQuality: 5, movementEfficiency: 5, riskLevel: 'medium' },
      findings: [],
      detailedAnalysis: responseText,
    };
  };

  // Reset form
  const handleReset = useCallback(() => {
    setUploadedVideo(null);
    setQuestion('');
    setAnalysis(null);
    setAnalysisResult(null);
    setAnalysisMetadata(null);
    setUploadProgress(0);
    setError(null);
    setNotification(null);
  }, []);

  // Submit video for analysis
  const handleSubmitForAnalysis = useCallback(async (options = {}) => {
    if (!uploadedVideo) {
      setError('Please upload a video first');
      return;
    }

    if (!question.trim()) {
      setError('Please enter a question about your technique');
      return;
    }

    setIsLoading(true);
    setAnalysis(null);
    setAnalysisResult(null);
    setAnalysisMetadata(null);
    setError(null);
    setUploadProgress(0);

    try {
      // Step 1: Upload video
      setUploadProgress(10);
      const formData = new FormData();
      formData.append('video', uploadedVideo);

      const uploadRes = await fetch(`${NODE_BACKEND_URL}/api/strength/upload-video`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error('Video upload failed');
      }

      const uploadData = await uploadRes.json();
      setUploadProgress(50);

      // Step 2: Analyze with Gemini via strength chat endpoint
      const combinedPrompt = `${STRUCTURED_ANALYSIS_PROMPT}\n\nUser's question: ${question}`;

      const analysisRes = await fetch(`${NODE_BACKEND_URL}/api/strengthDesign/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          message: combinedPrompt,
          fileUrl: uploadData.url || uploadData.filePath,
          history: [],
          analysisType: options.analysisType || 'general',
          frameRate: options.customFrameRate,
          startOffset: options.startOffset,
          endOffset: options.endOffset,
          useTimestamps: options.useTimestamps !== undefined ? options.useTimestamps : true,
        }),
      });

      setUploadProgress(80);

      if (!analysisRes.ok) {
        throw new Error('Analysis request failed');
      }

      const analysisData = await analysisRes.json();
      const responseText = analysisData.response || analysisData.analysis || analysisData.message || '';
      setUploadProgress(100);

      // Parse structured response (R6)
      const structured = parseStructuredResponse(responseText);

      setAnalysis(responseText);
      setAnalysisResult(structured);
      setAnalysisMetadata({
        processingModel: analysisData.model || 'gemini-3-flash-preview',
        analysisType: options.analysisType || 'general',
        frameRate: options.customFrameRate,
        videoClipping: {
          startOffset: options.startOffset,
          endOffset: options.endOffset,
        },
        timestampsUsed: options.useTimestamps !== false,
        videoSize: uploadedVideo.size,
      });
      setNotification({ type: 'success', message: 'Analysis complete!' });
    } catch (err) {
      console.error('Error during analysis:', err);
      setError(err.message || 'Failed to analyze video. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [uploadedVideo, question]);

  // Save analysis to user account
  const saveAnalysis = useCallback(async () => {
    const user = getUser();
    if (!user?.userId) {
      setError('You must be logged in to save analyses');
      return;
    }

    if (!analysis || !uploadedVideo || !question) {
      setError('Cannot save: Missing analysis data');
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${user.userId}/analyses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          question,
          analysis,
          analysisResult,
          videoName: uploadedVideo.name,
          metadata: analysisMetadata,
        }),
      });

      if (!res.ok) throw new Error('Failed to save analysis');

      setNotification({ type: 'success', message: 'Analysis saved!' });
    } catch (err) {
      console.error('Error saving analysis:', err);
      setError(err.message || 'Failed to save analysis');
    } finally {
      setIsSaving(false);
    }
  }, [analysis, analysisResult, analysisMetadata, uploadedVideo, question]);

  // Load saved analyses
  const loadAnalyses = useCallback(async () => {
    const user = getUser();
    if (!user?.userId) return;

    try {
      const res = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${user.userId}/analyses`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedAnalyses(data.result || []);
      }
    } catch (err) {
      console.error('Error loading analyses:', err);
    }
  }, []);

  return {
    isLoading,
    isSaving,
    uploadedVideo,
    setUploadedVideo,
    question,
    setQuestion,
    analysis,
    setAnalysis,
    analysisResult,
    analysisMetadata,
    uploadProgress,
    error,
    notification,
    handleReset,
    handleSubmitForAnalysis,
    saveAnalysis,
    savedAnalyses,
    loadAnalyses,
  };
}
